import { useState, useEffect, FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getInvitationByToken } from '../../services/invitationService';
import { registerWithInvitation, joinOrganizationWithInvitation, getOrganization } from '../../services/authService';
import { Invitation } from '@easydone/shared';
import {
  CheckCircleIcon,
  XCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { timestampToMillis } from '../../utils/timestamp';

export function JoinPage() {
  const { token } = useParams<{ token: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [orgName, setOrgName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  // Form state for new users
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    if (!token) {
      setValidationError('No invitation token provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const inv = await getInvitationByToken(token);

      if (!inv) {
        setValidationError('Invitation not found');
        setLoading(false);
        return;
      }

      if (!inv.isActive) {
        setValidationError('This invitation has been revoked');
        setLoading(false);
        return;
      }

      const expiresAt = timestampToMillis(inv.expiresAt);
      if (expiresAt < Date.now()) {
        setValidationError('This invitation has expired');
        setLoading(false);
        return;
      }

      if (inv.maxUses && inv.usedCount >= inv.maxUses) {
        setValidationError('This invitation has reached its maximum number of uses');
        setLoading(false);
        return;
      }

      setInvitation(inv);

      // Load organization name
      const org = await getOrganization(inv.orgId);
      if (org) {
        setOrgName(org.name);
      }
    } catch (err) {
      console.error('Failed to load invitation:', err);
      setValidationError('Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinExisting = async () => {
    if (!currentUser || !token) return;

    setSubmitting(true);
    setError('');

    try {
      await joinOrganizationWithInvitation(currentUser.uid, token);
      navigate('/');
    } catch (err: any) {
      console.error('Failed to join organization:', err);
      setError(err.message || 'Failed to join organization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterNew = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setError('');

    if (!displayName.trim()) {
      setError('Name is required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      await registerWithInvitation(email, password, displayName, token);
      navigate('/');
    } catch (err: any) {
      console.error('Registration error:', err);

      // Handle email already in use error
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in to join this organization.');
      } else {
        setError(err.message || 'Failed to create account');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (timestamp: any) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500">Loading invitation...</div>
      </div>
    );
  }

  if (validationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
            <p className="text-gray-600 mb-6">{validationError}</p>
            <Link to="/login" className="btn btn-primary">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  // If user is already logged in, show join existing flow
  if (currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                You've been invited!
              </h1>
              {invitation.customMessage && (
                <p className="text-gray-600 italic mb-4">"{invitation.customMessage}"</p>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Organization:</span>
                <span className="font-medium text-gray-900">{orgName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Role:</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  {invitation.role.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Expires:</span>
                <span className="text-gray-900">{formatDate(invitation.expiresAt)}</span>
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <button
              onClick={handleJoinExisting}
              disabled={submitting}
              className="w-full btn btn-primary disabled:opacity-50 mb-4"
            >
              {submitting ? 'Joining...' : `Join ${orgName}`}
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Not you?{' '}
                <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                  Sign out and use a different account
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If user is not logged in, show registration form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-4">
            <img
              src="/logo.png"
              alt="EasyDone-Pro"
              className="h-20 w-20"
            />
          </div>
          <h1 className="text-center text-3xl font-bold text-primary-600">
            EasyDone-Pro
          </h1>
          <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
            Join {orgName}
          </h2>
          {invitation.customMessage && (
            <p className="mt-2 text-center text-gray-600 italic">
              "{invitation.customMessage}"
            </p>
          )}
        </div>

        <div className="bg-primary-50 rounded-lg p-4">
          <div className="flex items-center space-x-3 text-sm">
            <UsersIcon className="w-5 h-5 text-primary-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-primary-900">
                You'll join as: {invitation.role.replace('_', ' ')}
              </p>
              <p className="text-primary-700 text-xs mt-1">
                Organization: {orgName}
              </p>
            </div>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleRegisterNew}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                Your Name
              </label>
              <input
                id="displayName"
                name="displayName"
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-1 input w-full"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 input w-full"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 input w-full"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 input w-full"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full btn btn-primary disabled:opacity-50"
            >
              {submitting ? 'Creating account...' : 'Create account and join'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to={`/login?redirect=/join/${token}`}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign in to join this organization
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
